import { Box, Text, VStack } from "@chakra-ui/layout";
import { Button, Tooltip } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type InvoicesForPaymentMatchQuery,
  OiWhereColumn,
  OutgoingInvoiceOrderByColumnEnum,
  OutgoingInvoiceTypeEnum,
  PaymentStatusEnum,
  SqlOperator,
} from "@src/__generated__/graphql";
import type { DetailTableCustomProps } from "@src/components/modules/bank/match-payment-modal";
import { BankStore } from "@src/components/modules/bank/stores";
import { AbraFlexiExportedIcon } from "@src/components/modules/common/exports/abraflexi/AbraFlexiExportedIcon";
import { XeroExportedIcon } from "@src/components/modules/common/exports/xero/Icon";
import { EmailLogs } from "@src/components/modules/invoices/EmailLogs";
import { withProjectHeaderTooltip } from "@src/components/modules/reports/project/common";
import {
  ACTIONS_COLUMN_ID,
  type BodyColumnDef,
  Link,
} from "@src/components/ui-kit";
import { PreviewAndDownloadCell } from "@src/components/widgets/TableCell";
import { getPaymentStatusFilterOptions } from "@src/utils/components/filters/constants";
import { Filter } from "@src/utils/components/filters/models";
import { currency, dateTime, formatDate } from "@src/utils/formatters";
import { camelCase, last } from "lodash";
import { Fragment } from "react";
import type { MatchPaymentTableItem } from "./table-state";

type InvoiceMatchRow = NonNullable<
  InvoicesForPaymentMatchQuery["outgoingInvoices"]
>["data"][0] &
  MatchPaymentTableItem;

export function createInvoiceFiltersArgs(): Filter<OiWhereColumn>[] {
  return [
    new Filter({
      column: OiWhereColumn.BrandId,
      operator: SqlOperator.In,
      title: t`Client/Brand`,
      tagTitle: t`Brand`,
      options: [],
      sectioned: true,
      hasSelectAllOption: true,
    }),
    new Filter({
      column: OiWhereColumn.WorkspaceBankAccountId,
      operator: SqlOperator.In,
      title: t`Agency bank account`,
      options: [],
    }),
    new Filter({
      column: OiWhereColumn.OwnerId,
      operator: SqlOperator.In,
      title: t`Project Manager`,
      options: [],
      hasSelectAllOption: true,
    }),
    new Filter({
      column: OiWhereColumn.PaymentStatus,
      operator: SqlOperator.In,
      title: t`Payment status`,
      options: getPaymentStatusFilterOptions(),
      value: [PaymentStatusEnum.NotPaid, PaymentStatusEnum.PartiallyPaid],
    }),
    new Filter({
      column: OiWhereColumn.Type,
      operator: SqlOperator.In,
      title: t`Type`,
      options: [
        {
          label: t`Invoice`,
          value: camelCase(OutgoingInvoiceTypeEnum.Invoice),
        },
        {
          label: t`Payment receipt`,
          value: camelCase(OutgoingInvoiceTypeEnum.ProformaPayment),
        },
        {
          label: t`Credit note`,
          value: camelCase(OutgoingInvoiceTypeEnum.CreditNote),
        },
      ],
    }),
    new Filter({
      column: OiWhereColumn.AmountFrom,
      operator: SqlOperator.Gte,
      title: t`Amount from`,
      options: [],
      mutateToUrl: BankStore.mutateAmountFilter,
      hidden: true,
    }),
    new Filter({
      column: OiWhereColumn.AmountTo,
      operator: SqlOperator.Lte,
      title: t`Amount to`,
      options: [],
      mutateToUrl: BankStore.mutateAmountFilter,
      hidden: true,
    }),
  ];
}

export function createInvoiceColumnsArgs(
  customProps: DetailTableCustomProps,
): BodyColumnDef<InvoiceMatchRow, OutgoingInvoiceOrderByColumnEnum>[] {
  return [
    {
      id: "invoice_no",
      meta: { label: t`Invoice no` },
      header: () => t`Invoice no`,
      orderBy: OutgoingInvoiceOrderByColumnEnum.InvoiceNo,
      cell: ({ row }) => (
        <div className="">
          <div className="flex items-center">
            <Link
              href={{
                pathname: "/invoices/outgoing-invoices/detail",
                query: { id: row.original.id },
              }}
            >
              {row.original.invoice_no}
            </Link>
            {row.original.abraflexi_exported && <AbraFlexiExportedIcon />}
            {row.original.xero_exported && <XeroExportedIcon />}

            <EmailLogs
              type="outgoingInvoice"
              logs={row.original.emailLogs.map((log) => ({
                ...log,
                sent_at: new Date(log.sent_at),
              }))}
            />
          </div>
          <p className="text-lg">
            {row.original.type === OutgoingInvoiceTypeEnum.ProformaPayment &&
              t`Payment receipt`}
          </p>
        </div>
      ),
    },
    {
      id: "brands",
      meta: { label: t`Brands` },
      header: () => t`Brands`,
      orderBy: OutgoingInvoiceOrderByColumnEnum.BrandName,
      cell: ({ row }) => (
        <Box maxW="100px" isTruncated>
          {row.original.brands.map((brand) => (
            <Fragment key={brand.id}>
              <Link
                href={{ pathname: "/brands/detail", query: { id: brand.id } }}
              >
                {brand.name}
              </Link>
              {last(row.original.brands)?.id === brand.id ? null : ", "}
            </Fragment>
          ))}
        </Box>
      ),
    },
    {
      id: "billing-address",
      meta: { label: t`Billing address` },
      header: () => t`Billing address`,
      cell: ({ row }) => {
        const fullAddress = `${row.original.billingEntity.legal_name}, ${row.original.billingEntity.street}, ${row.original.billingEntity.city}`;

        return (
          <Tooltip label={fullAddress} placement="top">
            <Box maxW="100px" isTruncated>
              {fullAddress}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      id: "projects",
      meta: { label: t`Project/s` },
      header: () => t`Project/s`,
      cell: ({ row }) => (
        <VStack alignItems="start" maxW="140px" spacing="2">
          {row.original.projects.map((project) => (
            <Tooltip
              key={project.id}
              hasArrow
              label={`${project.code} – ${project.title.replace(" ", "-")}`}
              placement="top-start"
            >
              <Box>
                <Link
                  href={{
                    pathname: "/projects/detail",
                    query: { id: project.id },
                  }}
                >
                  <div>{project.code}</div>
                  <Text maxW="120px" isTruncated>
                    {project.title}
                  </Text>
                </Link>
              </Box>
            </Tooltip>
          ))}
        </VStack>
      ),
    },
    {
      id: "amount_with_vat",
      meta: { label: t`Amount (VAT)` },
      header: () => t`Amount (VAT)`,
      orderBy: OutgoingInvoiceOrderByColumnEnum.AmountWithVat,
      cell: ({ row }) =>
        currency.formatByCurrency(
          row.original.stats.total_amount_with_vat_in_currency,
          row.original.currency,
        ),
    },
    {
      id: "billing_amount",
      meta: { label: t`Billing amount` },
      header: withProjectHeaderTooltip(
        t`Billing amount`,
        t`Amount credited to billing, at brand or project level.`,
      ),
      cell: ({ row }) =>
        currency.formatByCurrency(
          row.original.stats.billing_amount_in_currency,
          row.original.currency,
        ),
    },
    {
      id: "billing_amount_with_vat",
      meta: { label: t`Billing amount (VAT)` },
      header: withProjectHeaderTooltip(
        t`Billing amount (VAT)`,
        t`Amount credited to billing, at brand or project level.`,
      ),
      cell: ({ row }) =>
        currency.formatByCurrency(
          row.original.stats.billing_amount_with_vat_in_currency,
          row.original.currency,
        ),
    },
    {
      id: "issue_date",
      meta: { label: t`Issue date` },
      header: () => t`Issue date`,
      orderBy: OutgoingInvoiceOrderByColumnEnum.IssueDate,
      cell: ({ row }) => dateTime.format(new Date(row.original.issue_date)),
    },
    {
      id: "paid",
      meta: { label: t`Paid` },
      header: () => t`Paid`,
      cell: ({ row }) => {
        const paid = row.original.accountMoves?.reduce(
          (acc, curr) => acc + curr.amount,
          0,
        );
        if (!paid) return null;
        return currency.formatByCurrency(paid, row.original.currency);
      },
    },
    {
      id: "paid_at",
      meta: { label: t`Paid at` },
      header: () => t`Paid at`,
      orderBy: OutgoingInvoiceOrderByColumnEnum.PaymentStatus,
      cell: ({ row }) =>
        row.original.paid_at
          ? formatDate(new Date(row.original.paid_at))
          : t`Unpaid`,
    },
    {
      id: "invoicePdf",
      meta: { label: t`Invoice` },
      header: () => t`Invoice`,
      cell: ({ row }) => (
        <PreviewAndDownloadCell
          hideDownload
          value={[
            {
              label: t`Proforma invoice`,
              value: row.original.relatesToOutgoingProformaInvoice?.invoicePdf,
            },
            {
              label: t`Payment receipt`,
              value: row.original.invoicePdf,
            },
          ]}
        />
      ),
    },
    {
      id: ACTIONS_COLUMN_ID,
      meta: { label: t`Actions` },
      header: () => t`Actions`,
      size: 150,
      cell: ({ row }) => {
        const selectedRows = customProps.selectedRows();
        const isSelected = selectedRows.includes(row.original.id);

        return (
          <Button
            colorScheme={isSelected ? "red" : undefined}
            onClick={() => {
              customProps.onMatchClick(
                isSelected
                  ? selectedRows.filter((id) => id !== row.original.id)
                  : selectedRows.concat(row.original.id),
              );
            }}
            size="sm"
            variant="ghost"
          >
            {isSelected ? <Trans>Remove match</Trans> : <Trans>Match</Trans>}
          </Button>
        );
      },
    },
  ];
}
